import { Component, Input, Output, EventEmitter, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import * as icons from '@fortawesome/free-solid-svg-icons';
import { Tutorial, TutorialStep } from '../../shared/services/tutorial.service';
import { TutorialStateService } from '../../shared/services/tutorial-state.service';
import { TutorialModalService } from '../../shared/services/tutorial-modal.service';
import { TutorialActionType, TutorialActionPermission, NavigateAction } from '../../shared/models/tutorial-action.model';

declare var $: any;

/**
 * TutorialModalComponent - Displays step-by-step tutorials in a Bootstrap modal
 * 
 * Features:
 * - Modal navigation (previous/next/skip/finish)
 * - Interactive action buttons (navigate, toggle feature, custom actions)
 * - Keyboard navigation (arrow keys)
 * - Integration with TutorialStateService for persistence
 * - Accessibility features (ARIA attributes, focus management)
 * - Responsive design for mobile/tablet/desktop
 * - Subscribes to TutorialModalService for automatic show/hide
 * - Permission-based action button visibility
 */
@Component({
  selector: 'app-tutorial-modal',
  templateUrl: './tutorial-modal.component.html',
  styleUrls: ['./tutorial-modal.component.css']
})
export class TutorialModalComponent implements OnInit {
  // Font Awesome icons
  icons: any = icons;
  faArrowLeft = icons.faArrowLeft;
  faArrowRight = icons.faArrowRight;
  faCheck = icons.faCheck;
  faRocket = icons.faRocket;

  // Component inputs (can be overridden for testing)
  @Input() tutorial!: Tutorial;
  @Input() boardId!: string;
  @Input() userId!: string;
  @Input() userIsAdmin: boolean = false; // Permission flag from parent component

  // Component outputs
  @Output() completed = new EventEmitter<string>();
  @Output() dismissed = new EventEmitter<string>();
  @Output() closed = new EventEmitter<void>();
  @Output() actionTriggered = new EventEmitter<any>(); // Notify parent of custom actions

  // Component state
  currentStepIndex: number = 0;
  isVisible: boolean = false;
  actionInProgress: boolean = false;

  constructor(
    private tutorialStateService: TutorialStateService,
    private tutorialModalService: TutorialModalService,
    private router: Router
  ) { }

  ngOnInit(): void {
    // Subscribe to tutorial modal service state
    this.tutorialModalService.state$.subscribe(state => {
      if (state.tutorial) {
        this.tutorial = state.tutorial;
        this.boardId = state.boardId || '';
        this.userId = state.userId || '';
        this.userIsAdmin = state.userIsAdmin;
        this.isVisible = state.isVisible;
        
        if (state.isVisible) {
          this.show();
        } else {
          this.hide();
        }
      }
    });
  }

  /**
   * Get the current step being displayed
   * Returns undefined if tutorial is not yet loaded
   */
  get currentStep(): TutorialStep | undefined {
    return this.tutorial?.steps?.[this.currentStepIndex];
  }

  /**
   * Check if we're on the first step
   */
  get isFirstStep(): boolean {
    return this.currentStepIndex === 0;
  }

  /**
   * Check if we're on the last step
   */
  get isLastStep(): boolean {
    return this.tutorial && this.currentStepIndex === this.tutorial.steps.length - 1;
  }

  /**
   * Get the total number of steps
   */
  get totalSteps(): number {
    return this.tutorial?.steps.length || 0;
  }

  /**
   * Check if this is a single-step announcement (simplified UI)
   */
  get isSingleStep(): boolean {
    return this.totalSteps === 1;
  }

  /**
   * Get the current step number (1-indexed)
   */
  get currentStepNumber(): number {
    return this.currentStepIndex + 1;
  }

  /**
   * Check if the current step has an action button
   */
  get hasActionButton(): boolean {
    return !!this.currentStep?.actionButton;
  }

  /**
   * Check if the action button should be visible based on user permissions
   */
  get isActionButtonVisible(): boolean {
    if (!this.hasActionButton) {
      return false;
    }

    const permission = this.currentStep?.actionButton?.requiredPermission;
    
    switch (permission) {
      case TutorialActionPermission.PUBLIC:
        return true;
      case TutorialActionPermission.ADMIN:
        return this.userIsAdmin;
      case TutorialActionPermission.EDITOR:
        // For now, treat editor same as admin. Can be extended for granular permissions
        return this.userIsAdmin;
      case TutorialActionPermission.CUSTOM:
        // Component using this must handle custom permission validation
        return true;
      default:
        return false;
    }
  }

  /**
   * Get the icon for the action button
   * Safe method that handles undefined icon names
   */
  getActionButtonIcon(): any {
    const iconName = this.currentStep?.actionButton?.icon;
    if (!iconName || !(iconName in this.icons)) {
      return null;
    }
    return this.icons[iconName];
  }

  /**
   * Open the modal and reset to the first step
   */
  show(): void {
    this.currentStepIndex = 0;
    this.isVisible = true;
    setTimeout(() => {
      $('#modal_tutorial').modal('show');
    }, 0);
  }

  /**
   * Close the modal
   */
  hide(): void {
    this.isVisible = false;
    $('#modal_tutorial').modal('hide');
    this.closed.emit();
  }

  /**
   * Navigate to the next step, or complete the tutorial if on the last step
   */
  nextStep(): void {
    if (this.isLastStep) {
      this.completeTutorial();
    } else {
      this.currentStepIndex++;
    }
  }

  /**
   * Navigate to the previous step
   */
  previousStep(): void {
    if (!this.isFirstStep) {
      this.currentStepIndex--;
    }
  }

  /**
   * Skip the tutorial - marks it as dismissed
   */
  skipTutorial(): void {
    this.dismissTutorial();
  }

  /**
   * Handle "Show Me" click for single-step announcements
   * Triggers the action AND marks the tutorial as completed
   */
  handleShowMeClick(): void {
    // First trigger the action button behavior
    this.handleActionButtonClick();
    
    // Then complete the tutorial
    this.completeTutorial();
  }

  /**
   * Handle action button click - routes based on action type
   */
  handleActionButtonClick(): void {
    if (!this.hasActionButton || this.actionInProgress || !this.currentStep?.actionButton) {
      return;
    }

    const actionButton = this.currentStep.actionButton;
    const action = actionButton.action;

    this.actionInProgress = true;

    try {
      switch (action.type) {
        case TutorialActionType.NAVIGATE:
          this.handleNavigateAction(action as NavigateAction);
          break;
        case TutorialActionType.TOGGLE_FEATURE:
          this.handleToggleFeatureAction(action);
          break;
        case TutorialActionType.CUSTOM:
          this.handleCustomAction(action);
          break;
      }
    } finally {
      this.actionInProgress = false;
    }
  }

  /**
   * Handle navigation action - routes to specified page
   */
  private handleNavigateAction(action: NavigateAction): void {
    const route = this.replaceRouteParams(action.route);
    this.router.navigate([route], { queryParams: action.queryParams });
    
    // Keep tutorial open but allow user to see if they want
    // They can close it manually or continue browsing
  }

  /**
   * Handle feature toggle action
   */
  private handleToggleFeatureAction(action: any): void {
    // This would be implemented by extending the delivery config component
    // or using a dedicated feature toggle service
    this.actionTriggered.emit({
      type: TutorialActionType.TOGGLE_FEATURE,
      featureFlag: action.featureFlag
    });
  }

  /**
   * Handle custom action - emit event for parent to handle
   */
  private handleCustomAction(action: any): void {
    this.actionTriggered.emit({
      type: TutorialActionType.CUSTOM,
      actionId: action.actionId,
      payload: action.payload
    });
  }

   /**
     * Replace route parameters with actual values
     * Example: 'admin/config' becomes '/boards/:id/admin/config'
     */
    private replaceRouteParams(route: string): string {
      let result = route;
      
      // Replace common parameters
      result = result.replace(':boardId', this.boardId);
      result = result.replace(':id', this.boardId);
      result = result.replace(':userId', this.userId);

      // If route doesn't start with /, prepend board route
      if (!result.startsWith('/')) {
        // Check if it already contains the board ID (e.g., from :id replacement)
        // or if it already has a board path prefix
        if (!result.startsWith('boards/') && !result.includes(`/${this.boardId}`) && !result.startsWith(`${this.boardId}`)) {
          result = `/boards/${this.boardId}/${result}`;
        } else {
          result = `/${result}`;
        }
      }

      return result;
    }

  /**
   * Mark the tutorial as completed and close the modal
   * This is a fire-and-forget operation - we don't await the service call
   */
  private completeTutorial(): void {
    // Fire-and-forget: don't await this call
    this.tutorialStateService.markTutorialCompleted(
      this.boardId,
      this.userId,
      this.tutorial.id
    );
    
    // Emit event and close modal
    this.completed.emit(this.tutorial.id);
    this.tutorialModalService.hideTutorial();
  }

  /**
   * Mark the tutorial as dismissed and close the modal
   * This is a fire-and-forget operation - we don't await the service call
   */
  private dismissTutorial(): void {
    // Fire-and-forget: don't await this call
    this.tutorialStateService.markTutorialDismissed(
      this.boardId,
      this.userId,
      this.tutorial.id
    );
    
    // Emit event and close modal
    this.dismissed.emit(this.tutorial.id);
    this.tutorialModalService.hideTutorial();
  }

  /**
   * Handle keyboard navigation
   * Supports: ArrowRight/Down for next, ArrowLeft/Up for previous, ESC handled by Bootstrap
   */
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    // Don't handle keyboard if modal is not visible
    if (!this.isVisible) {
      return;
    }

    // Don't handle keyboard if focus is on an input or textarea
    if (event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement) {
      return;
    }

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        this.nextStep();
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        this.previousStep();
        break;
    }
  }
}

